import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import BrandLockup from "@/components/BrandLockup";
import AudienceDialog from "@/components/AudienceDialog";
import { APP_SITE } from "@/lib/links";

// danberrymusic.com nav. The Alephonics work now lives on its own site
// (Jewish Music Lab), linked out of the nav.
export const MARKETING_NAV = [
  {
    to: "/music",
    label: "Music",
    children: [
      { to: "/music", label: "Listen Now" },
      { to: "/podcast", label: "The Podcast" },
      { to: "/commissions", label: "Commissions" },
      { to: "/patrons", label: "Support the Music" },
    ],
  },
  {
    to: "/performances",
    label: "Events",
    children: [
      { to: "/performances", label: "Upcoming Performances" },
      { to: "/private-events", label: "Book a Lifecycle Event" },
      { to: "/community-events", label: "Book a Community Event" },
    ],
  },
  { href: APP_SITE, label: "Jewish Music Lab", external: true },
  { to: "/contact", label: "Contact" },
];

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function NavLink({ item, active }) {
  const isActive =
    active === item.to ||
    (item.children && item.children.some((c) => c.to === active));
  const activeStyle = { color: isActive ? "#F2D589" : undefined };

  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`subnav-${slug(item.label)}`}
        className="inline-flex items-center gap-1 transition-colors hover:text-[#F2D589] uppercase tracking-[0.2em] text-xs"
      >
        {item.label}
        <span aria-hidden="true" className="text-[11px]">
          &#8599;
        </span>
      </a>
    );
  }

  if (item.children) {
    return (
      <div className="relative group flex items-center">
        <Link
          to={item.to}
          data-testid={`subnav-${slug(item.label)}`}
          className="inline-flex items-center gap-1 transition-colors uppercase tracking-[0.2em] text-xs hover:text-[#F2D589]"
          style={activeStyle}
        >
          {item.label}
          <span aria-hidden="true" className="text-[9px] mt-0.5">
            &#9662;
          </span>
        </Link>
        <div className="absolute left-0 top-full z-50 pt-4 opacity-0 invisible transition-opacity duration-200 group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible">
          <div
            className="min-w-[15rem] py-2 rounded-xl overflow-hidden backdrop-blur-md"
            style={{
              backgroundColor: "rgba(12,8,38,0.95)",
              boxShadow: "inset 0 0 0 1px rgba(242,213,137,0.22)",
            }}
          >
            {item.children.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                data-testid={`subnav-${slug(c.label)}`}
                className="block px-5 py-2.5 uppercase tracking-[0.18em] text-[11px] whitespace-nowrap transition-colors hover:text-[#F2D589]"
                style={{ color: active === c.to ? "#F2D589" : "rgba(245,239,227,0.72)" }}
              >
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={item.to}
      data-testid={`subnav-${slug(item.label)}`}
      className="transition-colors hover:text-[#F2D589] uppercase tracking-[0.2em] text-xs"
      style={activeStyle}
    >
      {item.label}
    </Link>
  );
}

/**
 * Shared marketing-site navigation. Desktop: inline nav links + dropdown.
 * Mobile (<md): brand on the left, hamburger on the right.
 */
export default function MarketingNav({ active }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const brand = <BrandLockup markSize={60} />;

  const subscribeButton = (
    <AudienceDialog
      testIdPrefix="header-audience-dialog"
      source="header-subscribe"
      trigger={
        <button
          type="button"
          data-testid="header-subscribe-button"
          className="inline-flex items-center rounded-full px-5 h-9 text-xs uppercase transition-colors hover:brightness-110"
          style={{
            backgroundColor: "#F2D589",
            color: "#080419",
            letterSpacing: "0.2em",
            fontWeight: 600,
          }}
        >
          Subscribe
        </button>
      }
    />
  );

  return (
    <>
      <header className="max-w-[1200px] mx-auto px-6 sm:px-10 pt-6 sm:pt-8 flex items-center justify-between gap-4">
        {brand}

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-5 flex-wrap">
          {MARKETING_NAV.map((item) => (
            <NavLink key={item.to || item.href} item={item} active={active} />
          ))}
          {subscribeButton}
        </nav>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          data-testid="mobile-nav-open"
          className="md:hidden inline-flex flex-col items-center justify-center gap-[5px] h-10 w-10 rounded-full border border-white/15 hover:border-[#F2D589] transition-colors"
        >
          <span className="block w-4 h-px bg-current" />
          <span className="block w-4 h-px bg-current" />
          <span className="block w-4 h-px bg-current" />
        </button>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-[60] overflow-y-auto"
          data-testid="mobile-nav-drawer"
          style={{ backgroundColor: "#080419" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="px-6 pt-6 flex items-center justify-between">
            {brand}
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              data-testid="mobile-nav-close"
              className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/15 hover:border-[#F2D589] transition-colors text-xl leading-none"
              style={{ color: "#F5EFE3" }}
            >
              ×
            </button>
          </div>

          <nav className="px-6 mt-10 pb-16 flex flex-col gap-5" data-testid="mobile-nav-list">
            {MARKETING_NAV.map((item) => (
              <div key={item.to || item.href}>
                {item.external ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-subnav-${slug(item.label)}`}
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center gap-2 text-3xl py-1 transition-colors hover:text-[#F2D589]"
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontWeight: 500,
                      color: "#F5EFE3",
                    }}
                  >
                    {item.label}
                    <span aria-hidden="true" className="text-lg">
                      &#8599;
                    </span>
                  </a>
                ) : (
                  <Link
                    to={item.to}
                    data-testid={`mobile-subnav-${slug(item.label)}`}
                    onClick={() => setOpen(false)}
                    className="text-3xl py-1 transition-colors hover:text-[#F2D589]"
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontWeight: 500,
                      color: active === item.to ? "#F2D589" : "#F5EFE3",
                    }}
                  >
                    {item.label}
                  </Link>
                )}
                {item.children && (
                  <div className="mt-3 ml-1 flex flex-col gap-3">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`mobile-subnav-${slug(c.label)}`}
                        onClick={() => setOpen(false)}
                        className="uppercase tracking-[0.18em] text-[11px] transition-colors hover:text-[#F2D589]"
                        style={{ color: active === c.to ? "#F2D589" : "rgba(245,239,227,0.7)" }}
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="mt-6">{subscribeButton}</div>
          </nav>
        </div>
      )}
    </>
  );
}
